import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "RolesyPermisos";

const normalizarTexto = (texto = "") =>
  texto
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const AsignacionModulos = () => {
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [permisos, setPermisos] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingPermisos, setLoadingPermisos] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("usuarios");
          localStorage.removeItem("usuario");
          localStorage.removeItem("user");
          localStorage.removeItem("permisos");

          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const getModuloRolesPermisos = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulosStorage = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulosStorage.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloRolesPermisos = useMemo(() => getModuloRolesPermisos(), []);

  const puedeCrear =
    moduloRolesPermisos?.puedeCrear === true ||
    moduloRolesPermisos?.crear === true ||
    moduloRolesPermisos?.create === true;

  const puedeLeer =
    moduloRolesPermisos?.puedeLeer === true ||
    moduloRolesPermisos?.leer === true ||
    moduloRolesPermisos?.read === true;

  const puedeActualizar =
    moduloRolesPermisos?.puedeActualizar === true ||
    moduloRolesPermisos?.actualizar === true ||
    moduloRolesPermisos?.editar === true ||
    moduloRolesPermisos?.update === true;

  const esSuperAdmin = useMemo(() => {
    try {
      const usuariosStorage = JSON.parse(localStorage.getItem("usuarios")) || {};
      const usuarioStorage = JSON.parse(localStorage.getItem("usuario")) || {};
      const userStorage = JSON.parse(localStorage.getItem("user")) || {};
      const permisosStorage = JSON.parse(localStorage.getItem("permisos")) || {};

      const textoCompleto = normalizarTexto(
        JSON.stringify([
          usuariosStorage,
          usuarioStorage,
          userStorage,
          permisosStorage,
        ])
      );

      return (
        textoCompleto.includes("super_admin") ||
        textoCompleto.includes("superadmin")
      );
    } catch (error) {
      return false;
    }
  }, []);

  const accesoPermitido = esSuperAdmin || puedeLeer;

  const crearEstadoInicial = (listaModulos = []) => {
    const estado = {};

    listaModulos.forEach((modulo) => {
      estado[modulo._id] = {
        crear: false,
        leer: false,
        actualizar: false,
        eliminar: false,
      };
    });

    return estado;
  };

  const obtenerPermisosPorRol = useCallback(
    async (rolId, listaModulosActual = []) => {
      try {
        if (!rolId || !listaModulosActual.length) {
          setPermisos(crearEstadoInicial(listaModulosActual));
          return;
        }

        setLoadingPermisos(true);

        const { data } = await axios.get(
          `/api/seguridad/rolmodulo/rol/${rolId}`,
          authConfig()
        );

        const nuevoEstado = crearEstadoInicial(listaModulosActual);

        if (data?.ok && Array.isArray(data.modulos)) {
          data.modulos.forEach((modulo) => {
            const idModulo =
              modulo._id ||
              modulo.moduloObjectId?._id ||
              modulo.moduloObjectId;

            if (idModulo && nuevoEstado[idModulo]) {
              nuevoEstado[idModulo] = {
                crear: modulo.puedeCrear || false,
                leer: modulo.puedeLeer || false,
                actualizar: modulo.puedeActualizar || false,
                eliminar: modulo.puedeEliminar || false,
              };
            }
          });
        }

        setPermisos(nuevoEstado);
      } catch (error) {
        console.error("Error al obtener permisos del rol:", error);

        if (manejarErrorSesion(error)) return;

        setPermisos(crearEstadoInicial(listaModulosActual));

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            "No se pudieron cargar los permisos del rol.",
        });
      } finally {
        setLoadingPermisos(false);
      }
    },
    [manejarErrorSesion]
  );

  const getData = useCallback(async () => {
    if (!accesoPermitido) return;

    try {
      setLoading(true);

      const [rolesRes, modulosRes] = await Promise.all([
        axios.get("/api/seguridad/roles/list", authConfig()),
        axios.get("/api/seguridad/modulos/list", authConfig()),
      ]);

      const listaRoles =
        rolesRes.data?.ok && Array.isArray(rolesRes.data.roles)
          ? rolesRes.data.roles
          : [];

      const listaModulos =
        modulosRes.data?.ok && Array.isArray(modulosRes.data.modulos)
          ? modulosRes.data.modulos
          : [];

      setRoles(listaRoles);
      setModulos(listaModulos);
      setPermisos(crearEstadoInicial(listaModulos));

      if (listaRoles.length > 0) {
        const primerRol = listaRoles[0];

        setRolSeleccionado(primerRol);

        await obtenerPermisosPorRol(primerRol._id, listaModulos);
      }
    } catch (error) {
      console.error("Error al cargar roles y módulos:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "No se pudieron cargar los roles y módulos.",
      });

      setRoles([]);
      setModulos([]);
      setPermisos({});
    } finally {
      setLoading(false);
    }
  }, [accesoPermitido, manejarErrorSesion, obtenerPermisosPorRol]);

  useEffect(() => {
    getData();
  }, [getData]);

  const seleccionarRol = async (rol) => {
    setRolSeleccionado(rol);
    await obtenerPermisosPorRol(rol._id, modulos);
  };

  const editarRol = (e, rol) => {
    e.stopPropagation();

    if (!puedeActualizar && !esSuperAdmin) {
      return Swal.fire(
        "Sin permisos",
        "No tiene permisos para editar roles.",
        "warning"
      );
    }

    navigate(`/seguridad/roles/editar/${rol._id}`, {
      state: { rol },
    });
  };

  const editarModulo = (e, modulo) => {
    e.stopPropagation();

    if (!puedeActualizar && !esSuperAdmin) {
      return Swal.fire(
        "Sin permisos",
        "No tiene permisos para editar módulos.",
        "warning"
      );
    }

    navigate(`/seguridad/modulos/editar/${modulo._id}`, {
      state: { modulo },
    });
  };

  const handleCheck = (moduloId, permiso) => {
    if (!puedeActualizar && !esSuperAdmin) {
      return Swal.fire(
        "Sin permisos",
        "No tiene permisos para modificar permisos.",
        "warning"
      );
    }

    setPermisos((prev) => ({
      ...prev,
      [moduloId]: {
        ...prev[moduloId],
        [permiso]: !prev[moduloId]?.[permiso],
      },
    }));
  };

  const limpiarTodo = () => {
    if (!puedeActualizar && !esSuperAdmin) return;

    setPermisos(crearEstadoInicial(modulos));
  };

  const marcarTodo = () => {
    if (!puedeActualizar && !esSuperAdmin) return;

    const nuevoEstado = {};

    modulos.forEach((modulo) => {
      nuevoEstado[modulo._id] = {
        crear: true,
        leer: true,
        actualizar: true,
        eliminar: true,
      };
    });

    setPermisos(nuevoEstado);
  };

  const guardarAsignacion = async () => {
    try {
      if (!puedeActualizar && !esSuperAdmin) {
        return Swal.fire({
          icon: "warning",
          title: "Sin permisos",
          text: "No tiene permisos para guardar permisos.",
        });
      }

      if (!rolSeleccionado?._id) {
        return Swal.fire({
          icon: "warning",
          title: "Rol requerido",
          text: "Debes seleccionar un rol.",
        });
      }

      if (!modulos.length) {
        return Swal.fire({
          icon: "warning",
          title: "Sin módulos",
          text: "No hay módulos para asignar permisos.",
        });
      }

      setGuardando(true);

      const payload = {
        rolObjectId: rolSeleccionado._id,
        modulos: modulos.map((modulo) => ({
          moduloObjectId: modulo._id,
          puedeCrear: permisos[modulo._id]?.crear || false,
          puedeLeer: permisos[modulo._id]?.leer || false,
          puedeActualizar: permisos[modulo._id]?.actualizar || false,
          puedeEliminar: permisos[modulo._id]?.eliminar || false,
        })),
      };

      const { data } = await axios.post(
        "/api/seguridad/rolmodulo/save-permisos",
        payload,
        authConfig()
      );

      Swal.fire({
        icon: "success",
        title: data?.message || "Permisos guardados correctamente.",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Error al guardar asignación:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          error.response?.data?.message ||
          "No se pudieron guardar los permisos.",
      });
    } finally {
      setGuardando(false);
    }
  };

  if (!accesoPermitido) {
    return (
      <div className="bg-body-tertiary py-5">
        <div className="container">
          <div className="bg-white border rounded-4 shadow-sm p-5 text-center">
            <div className="display-6 mb-3">🔒</div>

            <h5 className="fw-semibold mb-2">Acceso restringido</h5>

            <p className="text-secondary mb-4">
              No tiene permisos para ingresar al módulo de roles y permisos.
            </p>

            <button
              className="btn btn-dark"
              type="button"
              onClick={() => navigate("/inicio")}
            >
              <i className="fa-solid fa-arrow-left me-2"></i>
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Asignación de Módulos y Permisos
                </h5>

                <small className="text-secondary">
                  Administra los permisos de cada rol sobre los módulos del sistema.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {(puedeCrear || esSuperAdmin) && (
                  <>
                    <button
                      className="btn btn-outline-dark"
                      type="button"
                      onClick={() => navigate("/seguridad/roles/registrar")}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-plus me-2"></i>
                      Nuevo rol
                    </button>

                    <button
                      className="btn btn-dark"
                      type="button"
                      onClick={() => navigate("/seguridad/modulos/registrar")}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-plus me-2"></i>
                      Nuevo módulo
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" />
                <div className="mt-3">Cargando roles y módulos…</div>
              </div>
            ) : (
              <div className="row g-4">
                <div className="col-12 col-lg-4">
                  <div className="border rounded-4 p-3 h-100">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0 fw-semibold">Roles del sistema</h6>
                        <small className="text-secondary">Selecciona un rol</small>
                      </div>

                      <span className="badge text-bg-light border text-secondary rounded-pill">
                        {roles.length} roles
                      </span>
                    </div>

                    <hr />

                    {roles.length > 0 ? (
                      <div className="d-flex flex-column gap-2">
                        {roles.map((rol) => {
                          const activo = rolSeleccionado?._id === rol._id;

                          return (
                            <div
                              key={rol._id}
                              role="button"
                              onClick={() => seleccionarRol(rol)}
                              className={`border rounded-4 px-3 py-3 ${
                                activo ? "bg-dark text-white" : "bg-light"
                              }`}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div>
                                  <div className="fw-semibold">
                                    {rol.nombreRol}
                                  </div>

                                  {rol.descripcionRol && (
                                    <div
                                      className={`small mt-1 ${
                                        activo ? "text-light" : "text-secondary"
                                      }`}
                                    >
                                      {rol.descripcionRol}
                                    </div>
                                  )}
                                </div>

                                {(puedeActualizar || esSuperAdmin) && (
                                  <button
                                    type="button"
                                    className={`btn btn-sm ${
                                      activo ? "btn-light" : "btn-outline-primary"
                                    }`}
                                    title="Editar rol"
                                    onClick={(e) => editarRol(e, rol)}
                                  >
                                    <i className="fa-solid fa-pen-to-square"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center text-secondary py-4">
                        No hay roles registrados.
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-8">
                  <div className="border rounded-4 p-3">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <h6 className="mb-0 fw-semibold">Módulos y permisos</h6>

                        <small className="text-secondary">
                          Rol actual:{" "}
                          <span className="fw-semibold">
                            {rolSeleccionado?.nombreRol || "Sin seleccionar"}
                          </span>
                        </small>
                      </div>

                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-outline-dark btn-sm"
                          onClick={marcarTodo}
                          disabled={!modulos.length || loadingPermisos}
                        >
                          <i className="fa-solid fa-check-double me-1"></i>
                          Marcar todo
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={limpiarTodo}
                          disabled={!modulos.length || loadingPermisos}
                        >
                          <i className="fa-solid fa-eraser me-1"></i>
                          Limpiar
                        </button>
                      </div>
                    </div>

                    <hr />

                    {loadingPermisos ? (
                      <div className="text-center text-secondary py-5">
                        <div className="spinner-border" role="status" />
                        <div className="mt-3">Cargando permisos…</div>
                      </div>
                    ) : modulos.length > 0 ? (
                      <div className="d-flex flex-column gap-3">
                        {modulos.map((modulo) => (
                          <div key={modulo._id} className="border rounded-4 p-3">
                            <div className="row align-items-center g-3">
                              <div className="col-12 col-md-4">
                                <div className="d-flex justify-content-between gap-2">
                                  <div>
                                    <div className="fw-semibold">
                                      {modulo.nombreModulo}
                                    </div>

                                    {modulo.descripcionModulo && (
                                      <div className="small text-secondary mt-1">
                                        {modulo.descripcionModulo}
                                      </div>
                                    )}
                                  </div>

                                  {(puedeActualizar || esSuperAdmin) && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      title="Editar módulo"
                                      onClick={(e) => editarModulo(e, modulo)}
                                    >
                                      <i className="fa-solid fa-pen-to-square"></i>
                                    </button>
                                  )}
                                </div>
                              </div>

                              {["crear", "leer", "actualizar", "eliminar"].map(
                                (permiso) => (
                                  <div
                                    key={permiso}
                                    className="col-6 col-md-2 text-md-center"
                                  >
                                    <label className="form-label small text-secondary text-uppercase d-md-none">
                                      {permiso}
                                    </label>

                                    <div>
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={
                                          permisos[modulo._id]?.[permiso] || false
                                        }
                                        onChange={() =>
                                          handleCheck(modulo._id, permiso)
                                        }
                                        disabled={
                                          !puedeActualizar && !esSuperAdmin
                                        }
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-secondary py-5">
                        No hay módulos registrados.
                      </div>
                    )}

                    <div className="row g-3 mt-3">
                      <div className="col-12 col-md-6">
                        <button
                          type="button"
                          className="btn btn-dark w-100 fw-semibold py-2"
                          onClick={guardarAsignacion}
                          disabled={
                            guardando ||
                            !rolSeleccionado ||
                            !modulos.length ||
                            (!puedeActualizar && !esSuperAdmin)
                          }
                        >
                          <i className="fa-solid fa-floppy-disk me-2"></i>
                          {guardando ? "Guardando..." : "Guardar asignación"}
                        </button>
                      </div>

                      <div className="col-12 col-md-6">
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100 fw-semibold py-2"
                          onClick={() => navigate("/inicio")}
                        >
                          <i className="fa-solid fa-arrow-left me-2"></i>
                          Volver
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 text-center text-secondary small">
          Verifica los permisos antes de guardar la asignación del rol seleccionado.
        </div>
      </div>
    </div>
  );
};

export default AsignacionModulos;